import { sendElastic } from 'actionCreators/judasLog';
import { ICourseHighlight } from 'interface/courseHighlight';
import { IShelf, enShelfType, IShowcaseCourse } from 'interface/showcase';
import { IUser } from 'interface/user';
import { useEffect, useRef, useState } from 'react';
import UpsellBanner from './UpsellBanner';
import Filters from './Filters';
import Shelves from './Shelves';
import AceleratorScript from 'components/Shared/AceleratorScript';
import { makeStyles, Typography } from '@material-ui/core';
import showcaseService, { TFilters } from 'services/showcase';
import CourseCard from './Shelves/CoursesShelf/CardCourse';
import InfinityScroll from 'components/Shared/InfinityScroll';
import SkeletonList from 'components/Shared/SkeletonList';
import { SvgIcon } from 'components/Shared/SvgIcon';
import PreviewContainer from 'components/Courses/Carousel/Preview';
import { useGlobalSearch } from 'hooks/globalSearch';
import { isMobile } from 'utils';
import { useAppSelector } from 'hooks/redux';
import { IInvite } from 'interface/invites';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    background: theme.palette.neutrals.black,
    minHeight: '100vh',
    '& .footer-root': {
      position: 'absolute',
      width: '100%',
      bottom: -54,
    },
  },
  searchList: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(6),
    alignContent: 'flex-start',
    width: '100%',
    padding: theme.spacing(0, 8),
    '& > div': {
      margin: theme.spacing(1),
    },
  },
  skeletonList: {
    maxWidth: '100%',
    overflow: 'scroll',
    padding: theme.spacing(6),
    boxSizing: 'border-box',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: theme.spacing(16),
    '& h2': {
      color: theme.palette.common.white,
      textAlign: 'center',
    },
  },
}));

const Courses = () => {
  const [searchResult, setSearchResult] = useState<IShowcaseCourse[]>(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const { searchText } = useGlobalSearch();
  const filterRef = useRef<TFilters>({ page: 1, total_pages: 0, search: '' });
  const pageLoaded = useRef(false);
  const classes = useStyles();
  const invites: IInvite[] = useAppSelector(({ showcaseInvite: { list } }) => list);
  const highlights: ICourseHighlight[] = useAppSelector(({ highlights }) => highlights);
  const user: IUser = useAppSelector(({ user }) => user);
  const shelves: IShelf[] = useAppSelector(({ shelves }) => shelves);

  const renderAceleratorScript = () => {
    const offer = shelves.find((s) => s.type === enShelfType.offer);
    if ((offer?.offers?.length ?? 0) > 0 || highlights.length > 0) {
      return <AceleratorScript />;
    }
    return null;
  };

  const redirectToPortalWhenThereIsOne = (shelves: IShelf[]) => {
    if (!shelves || shelves.length === 0) return;
    shelves = shelves.filter((shelve) => shelve.type !== enShelfType.offer);
    if (shelves.length === 1) {
      const { type, schools } = shelves[0];
      if (type === enShelfType.school && schools && schools.length === 1) {
        const shcoolUrl = schools[0].domain ?? schools[0].subdomain;
        window.location.href = shcoolUrl;
      }
    }
  };

  const fetchCourses = (params: TFilters) => {
    if (loading) return;
    setLoading(true);
    showcaseService.getCourses<IShowcaseCourse[]>({ ...params }).then(({ filters, data }) => {
      filterRef.current = filters;
      setSearchResult((results) => [...(results ?? []), ...data]);
      setLoading(false);
    });
  };

  const onScrollToBottom = () => {
    const filters = filterRef.current;
    const nextPage = filters.page + 1;
    if (nextPage > filters.total_pages) return;
    fetchCourses({ ...filterRef.current, page: nextPage });
  };

  const handleViewMode = (viewMode: string) => {
    if (viewMode === 'grid') {
      fetchCourses({ ...filterRef.current, search: undefined });
    } else {
      setSearchResult(null);
    }
  };
  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (!initialized) {
      return;
    }
    if (!Boolean(searchText.trim())) {
      return setSearchResult(null);
    }
    setLoading(true);
    showcaseService.getCourses<IShowcaseCourse[]>({ search: searchText }).then(({ filters, data }) => {
      filterRef.current = filters;
      setSearchResult(data);
      setLoading(false);
    });
  }, [searchText, initialized]);

  useEffect(() => {
    if (invites.length == 0) {
      return redirectToPortalWhenThereIsOne(shelves);
    }
    const hasSomething = shelves.every((shelf) => {
      const hasCourses = Boolean(shelf?.courses?.length ?? 0);
      const hasLessons = Boolean(shelf?.lessons?.length ?? 0);
      const hasOffers = Boolean(shelf?.offers?.length ?? 0);
      return hasCourses || hasLessons || hasOffers;
    });
    if (!pageLoaded.current && user.id && hasSomething) {
      pageLoaded.current = true;
      sendElastic('vitrine', 'Visualizou a vitrine');
    }
  }, [invites, shelves]);

  return (
    <div className={classes.container}>
      {renderAceleratorScript()}
      {!isMobile.any() && <UpsellBanner />}
      <Filters user={user} onChangeViewMode={handleViewMode} />
      <PreviewContainer>
        {!searchResult && !loading && <Shelves />}
        {!searchResult && loading && (
          <div className={classes.skeletonList}>
            <SkeletonList />
          </div>
        )}
        {Array.isArray(searchResult) && searchResult.length === 0 && !loading && (
          <div id='showcase-search-empty-state' className={classes.emptyContainer}>
            <SvgIcon name='empty' />
            <Typography variant='h2'>Ops, parece que não encontramos nada por aqui.</Typography>
          </div>
        )}
        {searchResult && (
          <div className={classes.searchList}>
            <InfinityScroll onScrollToBottom={onScrollToBottom}>
              {searchResult.map((course, key) => (
                <CourseCard key={key} course={course} />
              ))}
            </InfinityScroll>
            {loading && (
              <>
                <SkeletonList single={true} />
                <SkeletonList single={true} />
                <SkeletonList single={true} />
              </>
            )}
          </div>
        )}
      </PreviewContainer>
    </div>
  );
};

export default Courses;
